import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    input,
    Input,
    OnChanges,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { IWidgetConfig } from '../model/widget.model';
import { VisibilityEvaluator } from '@lib/util';
import { StorageKey, StorageService } from '@lib/core';

@Component({
    selector: 'widgets-overview',
    templateUrl: './widgets-overview.component.html',
    styleUrls: ['./widgets-overview.component.scss'],
    standalone: false
})
export class WidgetsOverviewComponent implements OnChanges, AfterViewInit {
    renderer = inject(Renderer2);
    storage = inject(StorageService);

    @Input() widgetsMatrix!: (IWidgetConfig | undefined)[][];
    readonly widgetsLoaded = input(false);

    @Output() widgetDropped = new EventEmitter<(number | undefined)[][]>();
    @Output() widgetDialogAdded = new EventEmitter<number>();
    @Output() itemByIdRemoved = new EventEmitter<number>();
    @Output() widgetExpanded = new EventEmitter<VisibilityEvaluator | undefined>();

    @ViewChildren('listItems') listItems!: QueryList<ElementRef>;

    changeLayoutModeActive = false;

    isDragging = false;

    showMessage!: boolean;

    debugModeStatus!: boolean;

    ngOnChanges(changes: SimpleChanges) {
        this.showMessageIsWidgetEmpty();
        if (changes && changes['widgetsMatrix']) {
            this.showMaxHeightElement();
            this.showMessageIsWidgetEmpty();
        }

        if (changes && changes['changeLayoutModeActive'] && !changes['changeLayoutModeActive'].firstChange) {
            this.showMaxHeightElement();
        }

        this.isDebugMode().then((status) => {
            this.debugModeStatus = status;
        });
    }

    ngAfterViewInit() {
        this.showMaxHeightElement();
    }

    showMessageIsWidgetEmpty() {
        this.showMessage = this.widgetsMatrix.every(
            (innerArray) => Array.isArray(innerArray) && innerArray.length === 0,
        );
    }

    setMaxHeightElement() {
        if (!this.listItems || this.listItems.length === 0) {
            return;
        }

        let maxHeight = 0;

        this.listItems.forEach((item) => {
            const height = item.nativeElement.offsetHeight;
            maxHeight = Math.max(maxHeight, height);
        });

        this.listItems.forEach((item) => {
            this.renderer.setStyle(item.nativeElement, 'height', maxHeight + 'px');
        });
    }

    changeLayoutMode() {
        this.changeLayoutModeActive = !this.changeLayoutModeActive;
    }

    addWidgetDialog(colIndex?: number) {
        if (!this.widgetsLoaded()) {
            return;
        }
        this.widgetDialogAdded.emit(colIndex);
    }

    onDrop(event: CdkDragDrop<(IWidgetConfig | undefined)[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
        }

        this.convertToIdMatrix();
    }

    deleteWidgetById(itemId: number) {
        this.itemByIdRemoved.emit(itemId);
        this.showMessageIsWidgetEmpty();
    }

    expandWidget(component: VisibilityEvaluator | undefined) {
        this.widgetExpanded.emit(component);
    }

    private async isDebugMode(): Promise<boolean> {
        const isDebug = (await this.storage.getItem(StorageKey.EPROC_DEBUG)) === 'true';
        return isDebug;
    }

    private convertToIdMatrix(): void {
        const idsMatrix = this.widgetsMatrix.map((x) => x.map((y) => y?.id));
        this.widgetDropped.emit(idsMatrix);

        this.showMaxHeightElement();
    }

    private showMaxHeightElement() {
        setTimeout(() => {
            this.setMaxHeightElement();
        }, 200);
    }
}
